import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Affixed, Field, Input, Select, Textarea } from '@/components/ui/Field'
import { DataList, DataRow, Page, Section } from '@/components/ui/Page'
import { ErrorState, Loading, Notice } from '@/components/ui/States'
import { ClientCard, DocumentTopBar, SaveState, projectLabel } from '@/features/documents/DocumentChrome'
import { LineItemsEditor, newLineKey } from '@/features/documents/LineItemsEditor'
import { usePricingRules } from '@/features/quotes/queries'
import { useSettings } from '@/features/settings/useSettings'
import { formatDateLong, formatDateShort, today, type IsoDate } from '@/lib/dates'
import { formatMoney } from '@/lib/format'
import { applyPayments, monthlyInstalments, outstanding, planInstalments, type PlannedInstalment } from '@/lib/invoicing'
import { lineTotals, type EditableLine } from '@/lib/lineItems'
import { describeError } from '@/lib/supabase'
import {
  useDeleteDraftInvoice,
  useDeletePayment,
  useGenerateInvoicePdf,
  useInvoice,
  usePaymentPlans,
  useRecordPayment,
  useReplaceInstalments,
  useSaveInvoiceDraft,
  useUpdateInvoice,
  planSteps,
  type InvoiceWithDetail,
  type Payment,
} from './queries'
import { InvoiceStatusBadge, METHOD_LABEL, invoiceNumber, invoiceTitle } from './shared'

/**
 * One invoice (29 Oct 2026), laid out like Pixieset's: the client, the items
 * (quantity × unit price), notes and the due date, then the payment plan and
 * what has been paid. A draft saves itself as she types; once sent, only
 * payments move. A plan's instalments keep their share when a draft's total
 * changes.
 */
export function InvoicePage() {
  const { invoiceId = '' } = useParams<{ invoiceId: string }>()
  const invoiceQuery = useInvoice(invoiceId)
  if (invoiceQuery.isPending) return <Loading label="Opening the invoice" />
  if (invoiceQuery.isError || !invoiceQuery.data) {
    return (
      <Page>
        <ErrorState title="That invoice could not load" detail={describeError(invoiceQuery.error)} onRetry={() => void invoiceQuery.refetch()} />
      </Page>
    )
  }
  return <InvoiceDetail key={invoiceQuery.data.id} invoice={invoiceQuery.data} />
}

const toEditable = (invoice: InvoiceWithDetail): EditableLine[] =>
  invoice.line_items.map((line) => ({
    key: newLineKey(),
    description: line.description,
    detail: line.detail ?? '',
    quantity: Number(line.quantity),
    unitPrice: Number(line.unit_price),
    source: 'manual',
    sourceId: null,
  }))

function InvoiceDetail({ invoice }: { invoice: InvoiceWithDetail }) {
  const navigate = useNavigate()
  const settings = useSettings()
  const update = useUpdateInvoice()
  const remove = useDeleteDraftInvoice()
  const pdf = useGenerateInvoicePdf()
  const save = useSaveInvoiceDraft()
  const rules = usePricingRules((invoice.job?.event_date ?? today()) as IsoDate)
  const [voiding, setVoiding] = useState(false)
  const [voidReason, setVoidReason] = useState('')

  const invoiceId = invoice.id
  const jobId = invoice.job_id
  const owed = outstanding(invoice)
  const isDraft = invoice.status === 'draft'
  const isVoid = invoice.status === 'void'

  const [lines, setLines] = useState<EditableLine[]>(() => toEditable(invoice))
  const [note, setNote] = useState(invoice.note ?? '')
  const [dueDate, setDueDate] = useState(invoice.due_date)
  const [touched, setTouched] = useState(false)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const vatRate = settings.data?.vat_registered ? Number(settings.data.vat_rate) : null
  const totals = lineTotals(lines, vatRate)

  const latest = useRef({ lines, note, dueDate, totals, instalments: invoice.instalments })
  latest.current = { lines, note, dueDate, totals, instalments: invoice.instalments }
  useEffect(() => {
    if (!touched || !isDraft) return
    const timer = window.setTimeout(() => {
      const now = latest.current
      if (now.dueDate < invoice.issue_date) return
      save.mutate(
        { invoiceId, jobId, lines: now.lines, totals: now.totals, note: now.note.trim() || null, dueDate: now.dueDate, instalments: now.instalments },
        { onSuccess: () => setSavedAt(new Date()) },
      )
    }, 900)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, note, dueDate, touched, isDraft])

  const edit = <T,>(setter: (value: T) => void) => (value: T) => {
    setTouched(true)
    setter(value)
  }

  const km =
    invoice.job?.distance_km === null || invoice.job?.distance_km === undefined
      ? null
      : Math.round(Number(invoice.job.distance_km) * (rules.data?.rules.travelChargesReturnTrip ? 2 : 1))

  async function openPdf() {
    // Opened before the await: Safari blocks window.open after one.
    const tab = window.open('', '_blank', 'noopener,noreferrer')
    try {
      if (isDraft && touched) {
        await save.mutateAsync({ invoiceId, jobId, lines, totals, note: note.trim() || null, dueDate, instalments: invoice.instalments })
      }
      const url = await pdf.mutateAsync({ invoiceId, jobId })
      if (tab) tab.location.href = url
      else window.location.href = url
    } catch {
      tab?.close()
    }
  }

  return (
    <Page className="max-w-[64rem]">
      <DocumentTopBar
        back="/invoices"
        title={`${invoiceTitle(invoice.kind)} ${invoiceNumber(invoice.invoice_number)}`}
        status={<InvoiceStatusBadge invoice={invoice} />}
        project={projectLabel(invoice.job)}
        projectTo={invoice.job ? `/weddings/${invoice.job.id}` : null}
        save={isDraft ? <SaveState saving={save.isPending} savedAt={savedAt} failed={save.isError} /> : null}
        actions={
          <>
            <Button loading={pdf.isPending} loadingLabel="Building" onClick={() => void openPdf()}>
              Open the PDF
            </Button>
            {isDraft ? (
              <Button
                variant="primary"
                disabled={totals.total <= 0 || save.isPending}
                loading={update.isPending}
                loadingLabel="Marking"
                onClick={() => update.mutate({ id: invoiceId, jobId, patch: { status: 'sent' }, success: 'Invoice marked as sent' })}
              >
                Mark as sent
              </Button>
            ) : null}
          </>
        }
      />

      {isDraft ? (
        <Notice className="mb-4" tone="amber" title="Not sent yet">
          Open the PDF and send it to them, then mark it as sent. Once sent, the items are fixed and only payments move.
        </Notice>
      ) : null}
      {isVoid ? (
        <Notice className="mb-4" tone="clay" title="Void">
          {invoice.void_reason}
        </Notice>
      ) : null}

      <Section>
        <ClientCard client={invoice.contact} />
      </Section>

      <Section title="Items">
        <LineItemsEditor
          lines={lines}
          onChange={edit(setLines)}
          readOnly={!isDraft}
          vatRate={vatRate}
          travel={{ ratePerKm: rules.data?.rules.travelRatePerKm ?? 0, km }}
        />
        {!isDraft ? (
          <dl className="mt-3 ml-auto grid max-w-[16rem] grid-cols-[1fr_auto] gap-x-6 gap-y-2 text-[length:var(--text-sm)]">
            <dt className="text-taupe">Paid</dt>
            <dd className="text-right" data-numeric>{formatMoney(Number(invoice.amount_paid))}</dd>
            <dt className="border-t pt-2">Still owed</dt>
            <dd className={owed > 0 ? 'border-t pt-2 text-right' : 'border-t pt-2 text-right text-sage'} data-numeric>{formatMoney(owed)}</dd>
          </dl>
        ) : null}
      </Section>

      <Section title="Notes">
        {isDraft ? (
          <Field label="Note on the invoice" hint="Optional. Printed under the amounts, in your words.">
            <Textarea rows={3} placeholder="Thank you for your business" value={note} onChange={(e) => edit(setNote)(e.target.value)} />
          </Field>
        ) : (
          <p className="text-[length:var(--text-sm)]">{invoice.note || <span className="text-taupe">No note.</span>}</p>
        )}
      </Section>

      {invoice.instalments.length === 0 ? (
        <Section title="Due date">
          {isDraft ? (
            <Field label="Payment due" className="max-w-[14rem]" {...(dueDate < invoice.issue_date ? { error: 'Not before the issue date.' } : {})}>
              <Input type="date" min={invoice.issue_date} value={dueDate} onChange={(e) => edit(setDueDate)(e.target.value)} />
            </Field>
          ) : (
            <p className="text-[length:var(--text-sm)]">{formatDateLong(invoice.due_date)}</p>
          )}
        </Section>
      ) : null}

      <Schedule invoice={invoice} jobId={jobId} eventDate={(invoice.job?.event_date ?? invoice.due_date) as IsoDate} editable={isDraft} />

      {!isVoid && !isDraft ? <Payments jobId={jobId} invoiceId={invoiceId} payments={invoice.payments} owed={owed} /> : null}

      {!isDraft ? (
        <Section title="Details">
          <DataList>
            <DataRow label="Issued">{formatDateShort(invoice.issue_date)}</DataRow>
            <DataRow label="Sent">{invoice.sent_at ? formatDateShort(invoice.sent_at.slice(0, 10)) : '—'}</DataRow>
            <DataRow label="Paid in full">{invoice.paid_at ? formatDateShort(invoice.paid_at.slice(0, 10)) : '—'}</DataRow>
          </DataList>
        </Section>
      ) : null}

      {!isVoid ? (
        <Section title={isDraft ? 'Delete' : 'Void'}>
          {isDraft ? (
            <>
              <p className="mb-3 text-[length:var(--text-sm)] text-taupe">A draft has never been sent, so it can be deleted outright.</p>
              <Button
                variant="danger"
                size="sm"
                loading={remove.isPending}
                onClick={async () => {
                  if (!window.confirm('Delete this draft invoice?')) return
                  await remove.mutateAsync({ id: invoiceId, jobId })
                  void navigate(jobId ? `/weddings/${jobId}` : '/invoices')
                }}
              >
                Delete this draft
              </Button>
            </>
          ) : voiding ? (
            <div className="flex flex-col gap-3">
              <Field label="Why is it void?" hint="Kept with the invoice. The number is never reused.">
                <Input value={voidReason} onChange={(event) => setVoidReason(event.target.value)} />
              </Field>
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  size="sm"
                  disabled={voidReason.trim() === '' || invoice.payments.length > 0}
                  onClick={() =>
                    update.mutate({ id: invoiceId, jobId, patch: { status: 'void', void_reason: voidReason.trim() }, success: 'Invoice voided' })
                  }
                >
                  Void invoice
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setVoiding(false)}>
                  Keep it
                </Button>
              </div>
              {invoice.payments.length > 0 ? (
                <p className="text-[length:var(--text-2xs)] text-clay">Remove its payments first: money cannot sit on a void invoice.</p>
              ) : null}
            </div>
          ) : (
            <>
              <p className="mb-3 text-[length:var(--text-sm)] text-taupe">
                A sent invoice is never deleted. Voiding keeps it on record, crossed out, with your reason.
              </p>
              <Button variant="danger" size="sm" onClick={() => setVoiding(true)}>
                Void this invoice
              </Button>
            </>
          )}
        </Section>
      ) : null}
    </Page>
  )
}

/**
 * The payment plan: each instalment, what has been paid against it, and
 * whether it is late. On a draft, the plan can be swapped for another of her
 * plans or for a custom monthly one (her couple who pays R3 290 a month).
 */
function Schedule({
  invoice,
  jobId,
  eventDate,
  editable,
}: {
  invoice: InvoiceWithDetail
  jobId: string | null
  eventDate: IsoDate
  editable: boolean
}) {
  const plansQuery = usePaymentPlans()
  const replace = useReplaceInstalments()
  const [mode, setMode] = useState<'view' | 'plan' | 'monthly'>('view')
  const [planId, setPlanId] = useState('')
  const [months, setMonths] = useState('6')
  const [firstDue, setFirstDue] = useState<string>(invoice.issue_date)

  const standings = applyPayments(invoice.instalments, invoice.amount_paid, today())
  const total = Number(invoice.total)
  const STATUS: Record<string, { label: string; tone: 'sage' | 'amber' | 'clay' | 'neutral' }> = {
    paid: { label: 'Paid', tone: 'sage' },
    part_paid: { label: 'Part paid', tone: 'amber' },
    due: { label: 'Due', tone: 'neutral' },
    overdue: { label: 'Overdue', tone: 'clay' },
  }

  const plan = (plansQuery.data ?? []).find((item) => item.id === planId) ?? null
  const preview: PlannedInstalment[] = (() => {
    try {
      if (mode === 'plan' && plan) return planInstalments(planSteps(plan), total, invoice.issue_date as IsoDate, eventDate).instalments
      if (mode === 'monthly') {
        const count = Number(months)
        if (Number.isInteger(count) && count >= 1 && count <= 36 && firstDue) return monthlyInstalments(total, count, firstDue)
      }
    } catch {
      /* an incomplete form: no preview */
    }
    return []
  })()

  async function apply() {
    if (preview.length === 0) return
    await replace.mutateAsync({
      invoiceId: invoice.id,
      jobId,
      instalments: preview,
      planName: mode === 'plan' ? (plan?.name ?? null) : `${months} monthly payments`,
    })
    setMode('view')
  }

  return (
    <Section
      title="Payment plan"
      {...(invoice.payment_plan_name ? { description: invoice.payment_plan_name } : {})}
      actions={
        editable && mode === 'view' ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setMode('plan')}>
              Use another plan
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMode('monthly')}>
              Monthly payments
            </Button>
          </>
        ) : null
      }
    >
      {standings.length === 0 ? (
        <p className="text-[length:var(--text-sm)] text-taupe">No schedule. The whole amount is due on {formatDateLong(invoice.due_date)}.</p>
      ) : (
        <ul className="flex flex-col">
          {standings.map((item) => (
            <li key={`${item.label}-${item.dueDate}`} className="flex items-baseline justify-between gap-3 border-b py-2">
              <span className="min-w-0 text-[length:var(--text-sm)]">
                {item.label}
                <span className="ml-2 text-[length:var(--text-2xs)] text-taupe">due {formatDateShort(item.dueDate)}</span>
              </span>
              <span className="flex shrink-0 items-baseline gap-3">
                {invoice.status === 'void' ? null : (
                  <Badge tone={STATUS[item.status]?.tone ?? 'neutral'}>{STATUS[item.status]?.label ?? item.status}</Badge>
                )}
                <span className="text-[length:var(--text-sm)]" data-numeric>
                  {formatMoney(item.amount)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {mode !== 'view' ? (
        <div className="mt-4 flex flex-col gap-3 border-l-2 border-l-[var(--color-rule-strong)] pl-4">
          {mode === 'plan' ? (
            <Field label="Plan">
              <Select value={planId} onChange={(event) => setPlanId(event.target.value)}>
                <option value="">Choose a plan</option>
                {(plansQuery.data ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Number of monthly payments">
                <Input type="number" min="1" max="36" value={months} onChange={(event) => setMonths(event.target.value)} />
              </Field>
              <Field label="First payment due">
                <Input type="date" min={invoice.issue_date} value={firstDue} onChange={(event) => setFirstDue(event.target.value)} />
              </Field>
            </div>
          )}
          {preview.length > 0 ? (
            <ul className="text-[length:var(--text-2xs)] text-taupe">
              {preview.map((item) => (
                <li key={`${item.label}-${item.dueDate}`} data-numeric>
                  {item.label}: {formatMoney(item.amount)}, due {formatDateLong(item.dueDate)}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="flex gap-2">
            <Button size="sm" variant="primary" disabled={preview.length === 0} loading={replace.isPending} onClick={() => void apply()}>
              Use this plan
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMode('view')}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </Section>
  )
}

function Payments({ jobId, invoiceId, payments, owed }: { jobId: string | null; invoiceId: string; payments: Payment[]; owed: number }) {
  const record = useRecordPayment()
  const remove = useDeletePayment()
  const [amount, setAmount] = useState('')
  const [paidOn, setPaidOn] = useState<string>(today())
  const [method, setMethod] = useState<Payment['method']>('eft')
  const [reference, setReference] = useState('')

  const parsed = Number(amount)
  const valid = amount.trim() !== '' && Number.isFinite(parsed) && parsed > 0

  async function submit() {
    if (!valid) return
    if (parsed > owed + 0.001) {
      toast.warning('That is more than is owed', { description: 'Recorded anyway: check the amount.' })
    }
    await record.mutateAsync({
      invoiceId,
      jobId,
      amount: Math.round(parsed * 100) / 100,
      paidOn,
      method,
      reference: reference.trim() || null,
    })
    setAmount('')
    setReference('')
  }

  return (
    <Section title="Payments">
      {payments.length === 0 ? (
        <p className="mb-4 text-[length:var(--text-sm)] text-taupe">Nothing received yet.</p>
      ) : (
        <ul className="mb-4 flex flex-col">
          {payments.map((payment) => (
            <li key={payment.id} className="flex items-baseline justify-between gap-3 border-b py-2">
              <span className="min-w-0 text-[length:var(--text-sm)]">
                {formatDateShort(payment.paid_on)}
                <span className="ml-2 text-[length:var(--text-2xs)] text-taupe">
                  {METHOD_LABEL[payment.method]}
                  {payment.reference ? ` · ${payment.reference}` : ''}
                </span>
              </span>
              <span className="flex shrink-0 items-baseline gap-3">
                <span className="text-[length:var(--text-sm)]" data-numeric>{formatMoney(Number(payment.amount))}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Remove this payment"
                  onClick={() => remove.mutate({ id: payment.id, invoiceId, jobId })}
                >
                  Remove
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {owed > 0 ? (
        <div className="grid items-end gap-3 sm:grid-cols-[8rem_9.5rem_7rem_minmax(0,1fr)_auto]">
          <Field label="Amount">
            <Affixed prefix="R">
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                placeholder={owed.toFixed(2)}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </Affixed>
          </Field>
          <Field label="Received on">
            <Input type="date" value={paidOn} max={today()} onChange={(event) => setPaidOn(event.target.value)} />
          </Field>
          <Field label="How">
            <Select value={method} onChange={(event) => setMethod(event.target.value as Payment['method'])}>
              {(Object.keys(METHOD_LABEL) as Array<Payment['method']>).map((key) => (
                <option key={key} value={key}>{METHOD_LABEL[key]}</option>
              ))}
            </Select>
          </Field>
          <Field label="Reference">
            <Input value={reference} placeholder="Bank reference" onChange={(event) => setReference(event.target.value)} />
          </Field>
          <Button variant="primary" disabled={!valid} loading={record.isPending} loadingLabel="Recording" onClick={() => void submit()}>
            Record payment
          </Button>
        </div>
      ) : null}
      {owed > 0 ? (
        <button
          type="button"
          className="mt-2 text-[length:var(--text-2xs)] text-taupe underline decoration-[var(--color-rule-strong)] underline-offset-4"
          onClick={() => setAmount(owed.toFixed(2))}
        >
          Paid in full ({formatMoney(owed)})
        </button>
      ) : null}
    </Section>
  )
}
